<script setup lang='ts'>
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import type { DistributionGroupWithID } from '#design-tokenomics/types'
import type { ReferenceProjectInfo } from '@forgd/contract/types'
import { formatValue } from '@forgd/formatter'
import { Button } from '@forgd/ui-kit/button'
import { Dialog, DialogContent } from '@forgd/ui-kit/dialog'
import { Label } from '@forgd/ui-kit/label'
import { ArrowLeftIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

const props = defineProps<{
  project: ReferenceProjectInfo
}>()

const emit = defineEmits(['back', 'save'])

const { data } = useQuery({
  ...buildApiQuery('/token-designer/reference-projects/{projectId}/groups', {
    params: () => ({
      path: {
        projectId: props.project.id,
      },
    }),
  }),
  refetchOnWindowFocus: false,
})

const allocationValues = computed<DistributionGroupWithID[]>(() => {
  return (data.value as DistributionGroupWithID[]) ?? []
})

function getColor(index: number) {
  return chartColors.tokenDesigner[index % chartColors.tokenDesigner.length]
}
const isOpen = ref(false)
function toggleModal() {
  isOpen.value = !isOpen.value
}

function save() {
  emit('save', allocationValues.value)
  toggleModal()
  emit('back')
}
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-white font-display">
    <div class="flex shrink-0 items-center justify-between border-b border-default bg-white p-4">
      <Button
        size="sm"
        variant="outline"
        @click="emit('back')"
      >
        <ArrowLeftIcon aria-hidden="true" />
        Back to List
      </Button>

      <Button
        size="sm"
        variant="ghost"
        icon-only
        aria-label="Close"
        @click="emit('back')"
      >
        <XMarkIcon aria-hidden="true" />
      </Button>
    </div>
    <div class="flex h-20 shrink-0 items-center gap-4 border-b border-default px-4">
      <img :src="resolveLogoWithFallback('project', project.image)" class="size-12 rounded-full" :alt="`${project.name} logo`">
      <div class="flex flex-col font-semibold text-primal">
        <div>
          Token Distribution Schedule
        </div>
        <div>
          {{ project?.name }}
        </div>
      </div>
    </div>
    <div class="flex items-center justify-between border-b border-default p-4 text-xs text-gray-600">
      <Label>Group Name</Label>
      <Label>Token Allocation %</Label>
    </div>
    <div class="flex shrink grow flex-col gap-3 overflow-y-scroll px-4 py-3">
      <div
        v-for="(allocation, idx) in allocationValues"
        :key="allocation.id"
        class="flex shrink-0 items-center justify-between text-sm text-primal"
      >
        <div class="flex items-center gap-4">
          <div
            class="size-3.5 rounded-full"
            :style="`background-color: ${getColor(idx)}`"
          />
          {{ allocation.name }}
        </div>
        <div>
          {{ formatValue(allocation?.allocationPercentage, 'percent:2') }}
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-4 border-t border-default p-4">
      <span class="text-sm text-gray-600">
        You can import the data in this template, and modify it to start creating your own token design.
      </span>
      <div class="grid grid-cols-2 gap-4">
        <Button
          block
          size="lg"
          @click="toggleModal"
        >
          Use This Template
        </Button>

        <Button
          block
          variant="outline"
          size="lg"
          @click="emit('back')"
        >
          Cancel
        </Button>
      </div>
    </div>
  </div>
  <Dialog :open="isOpen" @update:open="(open) => !open && toggleModal()">
    <DialogContent>
      <div class="flex flex-col items-center justify-center gap-2.5">
        <ExclamationCircleIcon class="size-10 text-accent" aria-hidden="true" />
        <span class="text-center text-xl font-semibold text-primal">
          You are about to use the data of this distribution schedule for your project
        </span>
        <span class="px-4 text-center text-secondary">
          Doing this will overwrite any progress you have made manually at this point.
        </span>
        <span class="mt-4 px-4 text-center text-secondary">
          Are you sure you want to continue?
        </span>
      </div>
      <div class="mt-6 grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          size="md"
          @click="toggleModal"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="md"
          @click="save"
        >
          Yes, Continue
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
